import React, { useEffect, useState, useRef } from "react";
import api, { apiError } from "@/lib/api";
import { wibToday } from "@/lib/bizdate";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, Loading, EmptyState, StatusBadge } from "@/components/common";
import { rupiah, formatDate, numberID } from "@/lib/format";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Plus, BookText, X } from "lucide-react";

const pageList = (cur, tp) => {
  if (tp <= 7) return Array.from({ length: tp }, (_, i) => i + 1);
  const out = [1];
  const s = Math.max(2, cur - 1), e = Math.min(tp - 1, cur + 1);
  if (s > 2) out.push("...");
  for (let i = s; i <= e; i++) out.push(i);
  if (e < tp - 1) out.push("...");
  out.push(tp);
  return out;
};

const LIMIT = 25;

export default function Journals() {
  const { can } = useAuth();
  const [rows, setRows] = useState(null);
  const [total, setTotal] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [page, setPage] = useState(1);
  const [input, setInput] = useState("");   // raw search box text
  const [q, setQ] = useState("");            // debounced, applied query
  const [err, setErr] = useState(false);
  const [open, setOpen] = useState(false);
  const reqRef = useRef(0);
  const load = (p, query) => {
    const seq = ++reqRef.current;            // guard: ignore stale responses
    setRows(null); setErr(false);
    api.get("/accounting/journals", { params: { page: p, limit: LIMIT, search: query || undefined } })
      .then((r) => { if (seq !== reqRef.current) return; setRows(r.data.rows); setTotal(r.data.total); setTotalPages(r.data.total_pages || Math.max(1, Math.ceil((r.data.total || 0) / LIMIT))); })
      .catch((e) => { if (seq !== reqRef.current) return; toast.error(apiError(e)); setErr(true); setRows([]); });
  };
  // Debounce typing ~350ms; any search change resets to page 1.
  useEffect(() => {
    const t = setTimeout(() => { setPage(1); setQ(input.trim()); }, 350);
    return () => clearTimeout(t);
  }, [input]);
  useEffect(() => { load(page, q); }, [page, q]);
  const act = async (id, path, ok) => { try { await api.post(`/accounting/journals/${id}/${path}`); toast.success(ok); load(page, q); } catch (e) { toast.error(apiError(e)); } };
  const start = total === 0 ? 0 : (page - 1) * LIMIT + 1;
  const end = Math.min(page * LIMIT, total);

  return (
    <div>
      <PageHeader title="Jurnal Akuntansi" subtitle="Sumber kebenaran akuntansi (double-entry). Debit harus sama dengan Kredit." testId="journals-header">
        {can("accounting.journal.manage") && <Button onClick={() => setOpen(true)} data-testid="add-journal-btn" className="bg-indigo-600 hover:bg-indigo-500"><Plus className="mr-1.5 h-4 w-4" /> Jurnal Manual</Button>}
      </PageHeader>
      <div className="relative mb-3 max-w-md">
        <Input value={input} onChange={(e) => setInput(e.target.value)} placeholder="Cari nomor jurnal, nomor referensi, atau deskripsi…" data-testid="journal-search" className="pr-9" />
        {input && <button type="button" onClick={() => setInput("")} data-testid="journal-search-clear" className="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 transition-colors hover:text-slate-700"><X className="h-4 w-4" /></button>}
      </div>
      <div className="rounded-xl border border-slate-200 bg-white shadow-sm overflow-x-auto">
        {!rows ? <Loading /> : err ? <EmptyState title="Gagal memuat data" subtitle="Terjadi kesalahan saat memuat jurnal. Coba lagi." icon={BookText} /> : rows.length === 0 ? <EmptyState title={q ? "Tidak ada jurnal yang cocok" : "Belum ada jurnal"} subtitle={q ? `Tidak ada hasil untuk “${q}”.` : undefined} icon={BookText} /> : (
          <Table>
            <TableHeader><TableRow><TableHead>No Jurnal</TableHead><TableHead>Tanggal</TableHead><TableHead>Deskripsi</TableHead><TableHead>Sumber</TableHead><TableHead className="text-right">Total</TableHead><TableHead>Status</TableHead><TableHead></TableHead></TableRow></TableHeader>
            <TableBody>
              {rows.map((j) => (
                <TableRow key={j.id} data-testid={`journal-row-${j.journal_no}`}>
                  <TableCell className="font-mono text-xs">{j.journal_no}</TableCell>
                  <TableCell className="text-xs text-slate-500">{formatDate(j.date)}</TableCell>
                  <TableCell className="max-w-[240px] truncate text-sm">{j.description}</TableCell>
                  <TableCell className="text-xs text-slate-400">{j.source_module}</TableCell>
                  <TableCell className="text-right tabular-nums">{rupiah(j.total_debit)}</TableCell>
                  <TableCell><StatusBadge status={j.status} /></TableCell>
                  <TableCell className="text-right">
                    {j.status === "Draft" && can("accounting.journal.post") && <Button size="sm" className="h-7 bg-emerald-600 text-xs hover:bg-emerald-500" onClick={() => act(j.id, "post", "Jurnal diposting")} data-testid={`post-${j.journal_no}`}>Posting</Button>}
                    {j.status === "Posted" && can("accounting.journal.post") && <Button size="sm" variant="outline" className="h-7 text-xs text-rose-600" onClick={() => act(j.id, "reverse", "Jurnal direverse")} data-testid={`reverse-${j.journal_no}`}>Reverse</Button>}
                    {j.status === "Draft" && can("accounting.journal.manage") && <Button size="sm" variant="ghost" className="h-7 text-xs text-slate-500" onClick={() => act(j.id, "cancel", "Jurnal dibatalkan")}>Batal</Button>}
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
        {rows && total > 0 && (
          <div className="flex flex-wrap items-center justify-between gap-3 border-t border-slate-100 px-4 py-3 text-sm text-slate-500">
            <span data-testid="journal-showing">Menampilkan {numberID(start)}–{numberID(end)} dari {numberID(total)} jurnal</span>
            <div className="flex items-center gap-1" data-testid="journal-pagination">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage(page - 1)} data-testid="page-prev">‹</Button>
              {pageList(page, totalPages).map((p, i) => p === "..."
                ? <span key={`e${i}`} className="px-2 text-slate-400">…</span>
                : <Button key={p} variant={p === page ? "default" : "outline"} size="sm" className={p === page ? "bg-indigo-600 hover:bg-indigo-500" : ""} onClick={() => setPage(p)} data-testid={`page-${p}`}>{p}</Button>)}
              <Button variant="outline" size="sm" disabled={page >= totalPages} onClick={() => setPage(page + 1)} data-testid="page-next">›</Button>
            </div>
          </div>
        )}
      </div>
      {open && <JournalDialog onClose={() => setOpen(false)} onSaved={() => { setOpen(false); setInput(""); setQ(""); setPage(1); load(1, ""); }} />}
    </div>
  );
}

function JournalDialog({ onClose, onSaved }) {
  const today = wibToday();
  const [f, setF] = useState({ date: today, description: "", reference: "" });
  const [coa, setCoa] = useState([]);
  const [lines, setLines] = useState([{ account_code: "", debit: 0, credit: 0 }, { account_code: "", debit: 0, credit: 0 }]);
  const [saving, setSaving] = useState(false);
  useEffect(() => { api.get("/accounting/chart-of-accounts").then((r) => setCoa(r.data.filter((a) => a.allow_posting))); }, []);
  const td = lines.reduce((s, l) => s + (Number(l.debit) || 0), 0);
  const tc = lines.reduce((s, l) => s + (Number(l.credit) || 0), 0);
  const balanced = td > 0 && Math.abs(td - tc) < 0.01;
  const setL = (i, p) => setLines(lines.map((x, idx) => idx === i ? { ...x, ...p } : x));
  const save = async () => {
    if (!f.description) return toast.error("Deskripsi wajib");
    if (!balanced) return toast.error("Total debit dan kredit harus sama.");
    setSaving(true);
    try {
      await api.post("/accounting/journals", { ...f, reference: f.reference || null, lines: lines.filter((l) => l.account_code && (Number(l.debit) || Number(l.credit))).map((l) => ({ account_code: l.account_code, debit: Number(l.debit) || 0, credit: Number(l.credit) || 0, memo: l.memo })) });
      toast.success("Jurnal draft dibuat"); onSaved();
    } catch (e) { toast.error(apiError(e)); } finally { setSaving(false); }
  };
  return (
    <Dialog open onOpenChange={onClose}>
      <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
        <DialogHeader><DialogTitle className="font-display">Jurnal Manual</DialogTitle></DialogHeader>
        <div className="grid grid-cols-3 gap-3">
          <div><Label className="text-xs text-slate-500">Tanggal</Label><Input type="date" className="mt-1" value={f.date} onChange={(e) => setF({ ...f, date: e.target.value })} data-testid="jr-date" /></div>
          <div className="col-span-2"><Label className="text-xs text-slate-500">Deskripsi</Label><Input className="mt-1" value={f.description} onChange={(e) => setF({ ...f, description: e.target.value })} data-testid="jr-desc" /></div>
        </div>
        <div className="mt-1 space-y-2">
          {lines.map((l, i) => (
            <div key={i} className="grid grid-cols-12 items-center gap-2">
              <div className="col-span-6"><Select value={l.account_code} onValueChange={(v) => setL(i, { account_code: v })}><SelectTrigger data-testid={`jr-acc-${i}`}><SelectValue placeholder="Pilih akun..." /></SelectTrigger><SelectContent>{coa.map((a) => <SelectItem key={a.code} value={a.code}>{a.code} - {a.name}</SelectItem>)}</SelectContent></Select></div>
              <Input type="number" className="col-span-2" placeholder="Debit" value={l.debit || ""} onChange={(e) => setL(i, { debit: e.target.value, credit: 0 })} data-testid={`jr-debit-${i}`} />
              <Input type="number" className="col-span-3" placeholder="Kredit" value={l.credit || ""} onChange={(e) => setL(i, { credit: e.target.value, debit: 0 })} data-testid={`jr-credit-${i}`} />
              <Button variant="ghost" size="sm" className="col-span-1 text-rose-500" onClick={() => setLines(lines.filter((_, idx) => idx !== i))}><X className="h-4 w-4" /></Button>
            </div>
          ))}
          <Button size="sm" variant="outline" onClick={() => setLines([...lines, { account_code: "", debit: 0, credit: 0 }])}><Plus className="mr-1 h-3.5 w-3.5" /> Baris</Button>
        </div>
        <div className={`mt-2 flex justify-end gap-6 text-sm font-semibold ${balanced ? "text-emerald-600" : "text-rose-600"}`} data-testid="jr-totals">
          <span>Debit: {rupiah(td)}</span><span>Kredit: {rupiah(tc)}</span><span>{balanced ? "Seimbang ✓" : "Belum seimbang"}</span>
        </div>
        <DialogFooter><Button variant="outline" onClick={onClose}>Batal</Button><Button onClick={save} disabled={saving || !balanced} data-testid="save-journal-btn" className="bg-indigo-600 hover:bg-indigo-500">{saving ? "..." : "Simpan Draft"}</Button></DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
